import type { FieldFormatter, FieldValidator } from '@lucidtech/las-sdk-browser';

import {
  FORMATTER_CLASSIFICATION_FUNCTION_ID,
  FORMATTER_DATE_FUNCTION_ID,
  FORMATTER_IBAN_NUMBER_FUNCTION_ID,
  FORMATTER_NUMERIC_FUNCTION_ID,
  VALIDATOR_BIC_SWIFT_FUNCTION_ID,
  VALIDATOR_CONFIDENCE_FUNCTION_ID,
  VALIDATOR_EMAIL_FUNCTION_ID,
  VALIDATOR_FREIGHT_CONTAINER_ID_FUNCTION_ID,
  VALIDATOR_IBAN_FUNCTION_ID,
  VALIDATOR_LICENSE_PLATE_FUNCTION_ID,
  VALIDATOR_PASSPORT_NUMBER_FUNCTION_ID,
  VALIDATOR_PHONE_NUMBER_FUNCTION_ID,
  VALIDATOR_TAX_ID_FUNCTION_ID,
  VALIDATOR_URL_FUNCTION_ID,
  VALIDATOR_VAT_ID_FUNCTION_ID,
} from '@/constants';

import country_codes from './country_codes.ts';
import currencies from './currencies.ts';

export const mediumConfidenceValidator: FieldValidator = {
  id: 'confidence-validator',
  functionId: VALIDATOR_CONFIDENCE_FUNCTION_ID,
  name: 'Confidence over 80%',
  description: 'Automatically validate all AI predictions where the confidence is over 80%',
  config: {
    thresholds: { automate: 0.8 },
  },
};

export const highConfidenceValidator: FieldValidator = {
  id: 'confidence-validator',
  functionId: VALIDATOR_CONFIDENCE_FUNCTION_ID,
  name: 'Confidence over 90%',
  description: 'Automatically validate all AI predictions where the confidence is over 90%',
  config: {
    thresholds: { automate: 0.9 },
  },
};

export const amountFormatter: FieldFormatter = {
  id: 'amount-formatter',
  functionId: FORMATTER_NUMERIC_FUNCTION_ID,
  name: 'Amount',
  description: 'Removes thousand separators and enforces two decimals',
  config: {},
};

export const currencyFormatter: FieldFormatter = {
  id: 'currency-formatter',
  functionId: FORMATTER_CLASSIFICATION_FUNCTION_ID,
  name: 'Currency',
  description: 'Enforce a valid ISO currency',
  config: {
    labels: currencies,
    catchAll: 'Unknown',
  },
};

export const genderFormatter: FieldFormatter = {
  id: 'gender-formatter',
  functionId: FORMATTER_CLASSIFICATION_FUNCTION_ID,
  name: 'Gender',
  description: 'Enforce a gender category',
  config: {
    labels: [
      { value: 'MALE', description: 'Male' },
      { value: 'FEMALE', description: 'Female' },
      { value: 'OTHER', description: 'Other' },
    ],
    catchAll: 'Unknown',
  },
};

export const countryCodeFormatter: FieldFormatter = {
  id: 'country-code-formatter',
  functionId: FORMATTER_CLASSIFICATION_FUNCTION_ID,
  name: 'ISO Alpha-2 country code',
  description: 'Enforce a valid ISO alpha-2 country code',
  config: {
    labels: country_codes,
    catchAll: 'Unknown',
  },
};

export const dateFormatter: FieldFormatter = {
  id: 'date-formatter',
  functionId: FORMATTER_DATE_FUNCTION_ID,
  name: 'ISO Date',
  description: 'Convert to an ISO date format (YYYY-MM-DD)',
  config: {},
};

export const numericFormatter: FieldFormatter = {
  id: 'numeric-formatter',
  functionId: FORMATTER_NUMERIC_FUNCTION_ID,
  name: 'Numeric',
  description: 'Removes thousand separators',
  config: {},
};

export const ibanValidator: FieldValidator = {
  id: 'iban-validator',
  functionId: VALIDATOR_IBAN_FUNCTION_ID,
  name: 'IBAN',
  description: 'Validate that the input is a valid IBAN number',
  config: {},
};

export const vatIdValidator: FieldValidator = {
  id: 'vat-id-validator',
  functionId: VALIDATOR_VAT_ID_FUNCTION_ID,
  name: 'VAT ID',
  description: 'Validate that the input is a valid VAT Identification Number',
  config: {},
};

export const taxIdValidator: FieldValidator = {
  id: 'tax-id-validator',
  functionId: VALIDATOR_TAX_ID_FUNCTION_ID,
  name: 'Tax ID',
  description: 'Validate that the input is a valid Tax Identification Number',
  config: {},
};

export const passportNumberValidator: FieldValidator = {
  id: 'passport-number-validator',
  functionId: VALIDATOR_PASSPORT_NUMBER_FUNCTION_ID,
  name: 'Passport Number',
  description: 'Validate that the input is a valid Passport Number',
  config: {},
};

export const licensePlateValidator: FieldValidator = {
  id: 'license-plate-validator',
  functionId: VALIDATOR_LICENSE_PLATE_FUNCTION_ID,
  name: 'License Plate',
  description: 'Validate that the input is a valid Vehicle License Plate',
  config: {},
};

export const phoneNumberValidator: FieldValidator = {
  id: 'phone-number-validator',
  functionId: VALIDATOR_PHONE_NUMBER_FUNCTION_ID,
  name: 'Phone Number',
  description: 'Validate that the input is a valid mobile phone number',
  config: {},
};

export const emailValidator: FieldValidator = {
  id: 'email-validator',
  functionId: VALIDATOR_EMAIL_FUNCTION_ID,
  name: 'Email',
  description: 'Validate that the input is a valid Email Address',
  config: {},
};

export const urlValidator: FieldValidator = {
  id: 'url-validator',
  functionId: VALIDATOR_URL_FUNCTION_ID,
  name: 'URL',
  description: 'Validate that the input is a valid Web Address',
  config: {},
};

export const freightContainerIdValidator: FieldValidator = {
  id: 'freight-container-id-validator',
  functionId: VALIDATOR_FREIGHT_CONTAINER_ID_FUNCTION_ID,
  name: 'Freight Container ID',
  description: 'Validate that the input is a valid ISO 6346 Freight Container ID',
  config: {},
};

export const bicSwiftValidator: FieldValidator = {
  id: 'bic-swift-validator',
  functionId: VALIDATOR_BIC_SWIFT_FUNCTION_ID,
  name: 'BIC/SWIFT',
  description: 'Validate that the input is a valid BIC/SWIFT code',
  config: {},
};
